import { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import type { AdminRole } from '../types/auth';

interface ProtectedRouteProps {
  children: ReactNode;
  requiredRoles?: AdminRole[];
}

/**
 * Default landing page for each role
 */
const DEFAULT_PAGES: Record<AdminRole, string> = {
  super_admin: '/admin/dashboard',
  creator: '/admin/spark-activities',
};

/**
 * Protected route wrapper that:
 * 1. Redirects to login if not authenticated
 * 2. Redirects to default page if user's role is not in requiredRoles
 */
export default function ProtectedRoute({ children, requiredRoles }: ProtectedRouteProps) {
  const { user, isAuthenticated, isLoading } = useAdminAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-admin-dark-950">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-admin-accent-blue mx-auto mb-4"></div>
          <p className="text-admin-slate-500">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    return <Navigate to="/admin/login" replace />;
  }

  // If requiredRoles is specified and user's role is not included, redirect to their default page
  if (requiredRoles && !requiredRoles.includes(user.role)) {
    const defaultPage = DEFAULT_PAGES[user.role] || '/admin/spark-activities';
    return <Navigate to={defaultPage} replace />;
  }

  return <>{children}</>;
}
